<ng-container *vsLet="canManageNotes$ | async as canManageNotes">
  <vs-card theme="default" *vsLet="meta$ | async as meta" class="block @container">
    <vs-card-header
      class="[&>div]:!flex-wrap [&>div>div:first-child]:!min-w-[200px] [&>div>div:first-child>div]:!flex-nowrap !py-3 @xl:!py-4 !px-4 @xl:!px-5"
      [config]="{ title: 'Notes', subtitle: 'Manage and view all notes in the table below.' }"
    >
      <vs-badge
        *vsLet="(meta?.totalItems ?? null) as totalItems"
        slot="after-title"
        [class.italic]="totalItems === null"
        [class.animate-pulse]="totalItems === null"
      >
        {{ totalItems === null ? 'loading' : (totalItems | number) + ' notes' }}
      </vs-badge>

      <button
        *ngIf="canManageNotes === true"
        vs-button
        size="md"
        slot="end"
        theme="primary"
        class="!w-full"
        (click)="new_note_dialog.open()"
      >
        <vs-icon class="hidden sm:inline-block" name="plus"></vs-icon>
        New Note
      </button>
    </vs-card-header>

    <vs-card-content class="!p-0 @container">
      <div class="mb-1 px-4 @xl:mb-1.5 @xl:mt-4 @xl:px-5">
        <input
          vsInput
          type="text"
          class="!shadow-none"
          placeholder="Search by title..."
          [formControl]="searchControl"
          [disabled]="!meta?.totalItems"
        />
      </div>

      <div #container *vsLet="loading$ | async as loading" class="relative">
        <div
          *vsLet="expandedNote$ | async as expandedNote"
          class="overflow-auto scrollbar-thin"
          [class.pointer-events-none]="loading"
        >
          <div id="table">
            <!-- Header -->
            <div id="header">
              <div class="cell"></div>

              <div class="cell">
                <p>Created</p>
              </div>

              <div class="cell">
                <p>Author</p>
              </div>

              <div class="cell">
                <p>Title</p>
              </div>

              <div class="cell">
                <p>Note</p>
              </div>

              <div class="cell"></div>
            </div>

            <ng-container *ngFor="let note of notes$ | async; trackBy: trackById; let even = even">
              <div class="row" [attr.data-even]="even">
                <!-- Pinned -->
                <div class="cell col-start-1 group" (click)="canManageNotes === true && togglePinned(note)">
                  <vs-icon
                    *ngIf="canManageNotes === true"
                    size="16"
                    name="pin-02"
                    class="transition-all group-hover:scale-[1.2] group-active:scale-95 {{ note.pinned ? 'text-primary-500 group-hover:text-primary-600' : 'text-gray-300 group-hover:text-gray-400' }}"
                  ></vs-icon>

                  <vs-icon
                    *ngIf="!canManageNotes"
                    size="16"
                    name="pin-02"
                    class="{{ note.pinned ? 'text-primary-500' : 'text-gray-300' }}"
                  ></vs-icon>
                </div>

                <!-- Created -->
                <div
                  class="cell"
                  tooltipStyle="light"
                  tooltipOpenDelay="150"
                  [vsTooltip]="{ title: 'Created on', subtitle: (note.created | date : 'medium') ?? '' }"
                  (click)="expand(note)"
                >
                  <p>{{ note.created | date : 'shortDate' }}</p>
                </div>

                <!-- Author -->
                <div
                  class="cell"
                  tooltipStyle="light"
                  tooltipOpenDelay="150"
                  [vsTooltip]="note.createdBy ? { title: 'Created by', subtitle: '<p>' + note.createdBy.displayName + '</p><p>' + note.createdBy.email + '</p>' }  : undefined"
                  (click)="expand(note)"
                >
                  <p *ngIf="note.createdBy">{{ note.createdBy.displayName }}</p>
                  <p *ngIf="!note.createdBy" class="text-gray-400 italic">System</p>
                </div>

                <!-- Title -->
                <div class="cell" (click)="expand(note)">
                  <p *ngIf="note.title && (note.title | trim)" class="max-w-xs">{{ note.title | trim }}</p>
                  <p *ngIf="!note.title || !(note.title | trim)" class="text-gray-400 italic">Untitled</p>
                </div>

                <!-- Note -->
                <div *vsLet="note.content | stripTags as content" class="cell" (click)="expand(note)">
                  <p *ngIf="content" class="max-w-sm">{{ content }}</p>
                  <p *ngIf="!content" class="text-gray-400 italic">No preview available</p>
                </div>

                <!-- Actions -->
                <div class="cell sticky right-0 z-10 gap-3" (click)="expand(note)">
                  <!-- Delete -->
                  <div
                    *ngIf="canManageNotes === true"
                    class="group py-2 px-3 -my-2 -mx-3 flex items-center"
                    (click)="$event.stopImmediatePropagation(); (deletingNoteId = note.id); delete_note_dialog.open()"
                  >
                    <vs-icon
                      size="16"
                      name="trash-01"
                      class="transition-all group-hover:scale-[1.2] group-active:scale-95 text-gray-500 group-hover:text-gray-600"
                    ></vs-icon>
                  </div>

                  <!-- Expand/Collapse -->
                  <div class="group py-2 px-3 -my-2 -mx-3 flex items-center">
                    <vs-icon
                      name="chevron-down"
                      size="16"
                      class="transition-all group-hover:scale-[1.2] group-active:scale-95 text-gray-500 group-hover:text-gray-600"
                      [class.-rotate-180]="expandedNote?.id === note.id"
                    ></vs-icon>
                  </div>
                </div>
              </div>

              <!-- Contents -->
              <div
                class="sticky left-0 grid transition-all will-change-transform w-0"
                [style.grid-template-rows]="note.id === expandedNote?.id ? '1fr' : '0fr'"
              >
                <div class="overflow-hidden opacity-0 pointer-events-none">
                  <div class="max-h-96 border-b px-6 py-2 overflow-visible" [style.width.px]="container.offsetWidth">
                    <div [innerHTML]="note.content | safe : 'html'"></div>
                  </div>
                </div>

                <div
                  class="absolute top-0 left-0 grid transition-all"
                  [style.width.px]="container.offsetWidth"
                  [style.grid-template-rows]="note.id === expandedNote?.id ? '1fr' : '0fr'"
                >
                  <div class="overflow-hidden">
                    <div class="max-h-96 border-b border-gray-200 px-6 py-2 overflow-auto scrollbar-thin">
                      <div [innerHTML]="note.content | safe : 'html'"></div>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="loading" class="absolute inset-0 bg-gray-50/25 z-20 overflow-hidden">
          <div class="relative w-full h-[3px] mt-[32px]">
            <div class="absolute inset-0 bg-primary-100"></div>
            <div id="indeterminate-progress-bar-value" class="absolute w-full h-full bg-primary-400"></div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          *vsLet="(!loading && meta?.totalItems === 0) as empty"
          class="text-center py-8 overflow-hidden transition-opacity opacity-0"
          [class.hidden]="!!meta?.totalItems"
          [class.!opacity-100]="empty"
        >
          <h1 class="whitespace-nowrap font-semibold text-gray-700">No linked notes</h1>
          <p class="whitespace-nowrap text-sm text-gray-700">Add a note to get started</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="rounded-xl overflow-hidden">
        <vs-pagination
          [config]="paginationConfig$ | async"
          (pagination)="pagination$.next({ page: $event.currentPage, limit: $event.itemsPerPage })"
        ></vs-pagination>
      </div>
    </vs-card-content>
  </vs-card>
</ng-container>

<ws-new-note-dialog #new_note_dialog [distributorId]="distributorId" (created)="refresh()"></ws-new-note-dialog>

<vs-dialog #delete_note_dialog [config]="{ dialogContainerClass: 'max-w-sm' }">
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      showDivider: false,
      hideCloseButton: true,

      icon: 'file-minus-02',
      iconTheme: 'danger',

      title: 'Remove note',
      subtitle: 'Delete this note. Careful, deleted notes cannot be restored.'
    }"
  ></vs-dialog-header>

  <div class="mt-4 flex flex-wrap gap-2 items-center">
    <button vs-button class="flex-1" appearance="outline" (click)="(deletingNoteId = null); delete_note_dialog.close()">
      Cancel
    </button>

    <button vs-button class="flex-1" theme="danger" (click)="deleteNote(); delete_note_dialog.close()">Remove</button>
  </div>
</vs-dialog>
